import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Typography } from '@mui/material'

import { useGetDashboardOutdatedGamesQuery, useGetGameNotesQuery } from '../../../../api/dashboard'
import { filterRowsByAnalyst, filterRowsByName, toOutdatedRows } from '../../helpers/dashboardHelpers'
import { useDashboardAnalysts } from '../../hooks/useDashboardAnalysts'
import { DashboardAction, DashboardColumn, DashboardGameRow, DashboardListDialogProps, DashboardVersion, allAnalystsId } from '../../types/Dashboard'
import { AnalystFilterSelect } from '../AnalystFilterSelect/AnalystFilterSelect'
import { DashboardGamesDialog } from '../DashboardGamesDialog/DashboardGamesDialog'
import { DashboardGamesTable } from '../DashboardGamesTable/DashboardGamesTable'
import { ReleaseNotesDialog } from '../ReleaseNotesDialog/ReleaseNotesDialog'

const columns = [
  DashboardColumn.Analyst,
  DashboardColumn.Icon,
  DashboardColumn.Name,
  DashboardColumn.Publisher,
  DashboardColumn.Subgenre,
  DashboardColumn.Rank,
  DashboardColumn.FirstUnanalyzedVersion,
]
const actions = [DashboardAction.ViewAnalyses, DashboardAction.Edit]

export const OutdatedGamesDialog: FC<DashboardListDialogProps> = ({ open, marketIso, onClose, onAction, storedSubgenres }) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [analystId, setAnalystId] = useState(allAnalystsId)
  const [releaseNotes, setReleaseNotes] = useState<DashboardVersion>()
  useEffect(() => {
    setSearch('')
    setAnalystId(allAnalystsId)
  }, [open])

  const { analystMap, analysts, analystsByAppId } = useDashboardAnalysts()
  const outdatedQuery = useGetDashboardOutdatedGamesQuery(marketIso, { skip: !open, refetchOnMountOrArgChange: true })
  const gameIds = useMemo(() => (outdatedQuery.data?.games ?? []).map(({ id }) => id), [outdatedQuery.data])
  const notesQuery = useGetGameNotesQuery(gameIds, { skip: !open || !gameIds.length })

  const rows = useMemo(
    () => (outdatedQuery.data ? toOutdatedRows(outdatedQuery.data.games, outdatedQuery.data.versions, marketIso) : []),
    [marketIso, outdatedQuery.data]
  )
  const shownRows = useMemo(() => filterRowsByName(filterRowsByAnalyst(rows, analystMap, analystId), search), [analystId, analystMap, rows, search])

  return (
    <>
      <DashboardGamesDialog
        open={open}
        title={t('internal-analysts:dashboard_outdated_title')}
        onClose={onClose}
        search={search}
        onSearchChange={setSearch}
        filters={<AnalystFilterSelect analysts={analysts} value={analystId} onChange={setAnalystId} />}
        isLoading={outdatedQuery.isFetching}
        error={outdatedQuery.isError ? t('internal-analysts:dashboard_list_failed') : undefined}
        summary={
          <>
            <Typography>{t('internal-analysts:dashboard_games_count', { count: rows.length })}</Typography>
            {notesQuery.isError && <Typography color="error.main">{t('internal-analysts:dashboard_notes_failed')}</Typography>}
          </>
        }
      >
        <DashboardGamesTable
          rows={shownRows}
          columns={columns}
          actions={actions}
          onAction={onAction}
          analystsByAppId={analystsByAppId}
          notes={notesQuery.data}
          storedSubgenres={storedSubgenres}
          onShowReleaseNotes={(row: DashboardGameRow) => setReleaseNotes(row.firstUnanalyzedVersion)}
          sortByRank
          noRowsLabel={t('internal-analysts:dashboard_no_games')}
        />
      </DashboardGamesDialog>
      <ReleaseNotesDialog version={releaseNotes} onClose={() => setReleaseNotes(undefined)} />
    </>
  )
}
